/**
 * ECDSA signature format conversion.
 *
 * Web Crypto produces and consumes IEEE P1363 signatures (fixed-width r||s), while the LMS
 * (`SHA384withECDSA`) and Node's `crypto.createSign` produce and consume ASN.1 DER
 * (SEQUENCE { INTEGER r, INTEGER s }). DER is the wire format for Bayun signatures, so the
 * Web Crypto paths convert on the way in and out.
 */

/** Component byte length of an r/s value for a given curve. */
export const ECDSA_COMPONENT_LENGTH = {
  "P-384": 48,
};

function stripLeadingZeros(bytes) {
  let start = 0;
  while (start < bytes.length - 1 && bytes[start] === 0) {
    start += 1;
  }
  return bytes.subarray(start);
}

function encodeDerInteger(component) {
  const trimmed = stripLeadingZeros(component);
  const needsPad = (trimmed[0] & 0x80) !== 0;
  const content = new Uint8Array(trimmed.length + (needsPad ? 1 : 0));
  content.set(trimmed, needsPad ? 1 : 0);

  const header = new Uint8Array([0x02, content.length]);
  const encoded = new Uint8Array(header.length + content.length);
  encoded.set(header, 0);
  encoded.set(content, header.length);
  return encoded;
}

function encodeDerLength(length) {
  if (length < 0x80) {
    return new Uint8Array([length]);
  }
  if (length <= 0xff) {
    return new Uint8Array([0x81, length]);
  }
  return new Uint8Array([0x82, (length >> 8) & 0xff, length & 0xff]);
}

/**
 * Converts an IEEE P1363 (r||s) signature into ASN.1 DER.
 *
 * @param {Uint8Array} p1363Signature Fixed-width r||s signature.
 * @returns {Uint8Array} DER-encoded signature.
 */
export function p1363ToDer(p1363Signature) {
  if (p1363Signature.length % 2 !== 0) {
    throw new Error("Invalid P1363 ECDSA signature length");
  }

  const half = p1363Signature.length / 2;
  const r = encodeDerInteger(p1363Signature.subarray(0, half));
  const s = encodeDerInteger(p1363Signature.subarray(half));

  const body = new Uint8Array(r.length + s.length);
  body.set(r, 0);
  body.set(s, r.length);

  const lengthBytes = encodeDerLength(body.length);
  const der = new Uint8Array(1 + lengthBytes.length + body.length);
  der[0] = 0x30;
  der.set(lengthBytes, 1);
  der.set(body, 1 + lengthBytes.length);
  return der;
}

function readDerLength(bytes, offset) {
  const first = bytes[offset];
  if (first < 0x80) {
    return { length: first, nextOffset: offset + 1 };
  }
  const byteCount = first & 0x7f;
  // Cap at 2: an ECDSA signature body is at most a few hundred bytes, and allowing 4 lets
  // `length` overflow into a negative int32 below, which defeats the bounds checks.
  if (byteCount === 0 || byteCount > 2 || offset + 1 + byteCount > bytes.length) {
    throw new Error("Invalid DER length");
  }
  let length = 0;
  for (let i = 0; i < byteCount; i += 1) {
    length = (length << 8) | bytes[offset + 1 + i];
  }
  // Reject non-minimal long-form lengths (e.g. 0x81 0x05, which must be encoded as 0x05).
  if (length < 0x80) {
    throw new Error("Invalid DER length: non-minimal encoding");
  }
  return { length, nextOffset: offset + 1 + byteCount };
}

function readDerInteger(bytes, offset) {
  if (bytes[offset] !== 0x02) {
    throw new Error("Invalid DER ECDSA signature: expected INTEGER");
  }
  const { length, nextOffset } = readDerLength(bytes, offset + 1);
  const end = nextOffset + length;
  if (length === 0 || end > bytes.length) {
    throw new Error("Invalid DER ECDSA signature: truncated INTEGER");
  }
  const value = bytes.subarray(nextOffset, end);
  // DER INTEGERs are signed and minimally encoded. r and s are always positive, so a set high
  // bit means a negative integer, and a leading 0x00 is only legal to clear an otherwise-set
  // high bit. Accepting either lets an attacker re-encode one signature many different ways.
  if ((value[0] & 0x80) !== 0) {
    throw new Error("Invalid DER ECDSA signature: negative INTEGER");
  }
  if (value.length > 1 && value[0] === 0x00 && (value[1] & 0x80) === 0) {
    throw new Error("Invalid DER ECDSA signature: non-minimal INTEGER");
  }
  return { value, nextOffset: end };
}

/**
 * Converts an ASN.1 DER signature into IEEE P1363 (r||s).
 *
 * @param {Uint8Array} derSignature DER-encoded signature.
 * @param {Number} componentLength Byte length of each of r and s for the curve.
 * @returns {Uint8Array} Fixed-width r||s signature.
 */
export function derToP1363(derSignature, componentLength) {
  if (derSignature[0] !== 0x30) {
    throw new Error("Invalid DER ECDSA signature: expected SEQUENCE");
  }

  const { length: bodyLength, nextOffset: bodyOffset } = readDerLength(
    derSignature,
    1,
  );
  // The declared SEQUENCE length must account for exactly the remaining bytes. Without this,
  // trailing data after a valid signature is silently ignored, so one signature can be padded
  // into unlimited distinct byte-strings that all verify.
  if (bodyOffset + bodyLength !== derSignature.length) {
    throw new Error(
      "Invalid DER ECDSA signature: length mismatch or trailing data",
    );
  }

  const r = readDerInteger(derSignature, bodyOffset);
  const s = readDerInteger(derSignature, r.nextOffset);
  if (s.nextOffset !== derSignature.length) {
    throw new Error("Invalid DER ECDSA signature: trailing data");
  }

  const rBytes = stripLeadingZeros(r.value);
  const sBytes = stripLeadingZeros(s.value);
  if (rBytes.length > componentLength || sBytes.length > componentLength) {
    throw new Error("Invalid DER ECDSA signature: component too large for curve");
  }

  const p1363 = new Uint8Array(componentLength * 2);
  p1363.set(rBytes, componentLength - rBytes.length);
  p1363.set(sBytes, componentLength * 2 - sBytes.length);
  return p1363;
}

/**
 * Normalizes a DER signature into IEEE P1363 for Web Crypto verification.
 *
 * DER is the only wire encoding Bayun uses: the signing paths always emit DER (Node's
 * `createSign` natively, Web Crypto via `p1363ToDer`), and the LMS `SHA384withECDSA` both
 * produces and expects DER. Accepting a bare P1363 blob here would make the browser verify
 * signatures that Node and Java reject, so the input must be strict DER.
 *
 * @param {Uint8Array} signature DER-encoded signature.
 * @param {Number} componentLength Byte length of each of r and s for the curve.
 * @returns {Uint8Array} Fixed-width r||s signature.
 */
export function toP1363(signature, componentLength) {
  return derToP1363(signature, componentLength);
}
